<template>
  <div>
    <ApiError :errors="errors"></ApiError>
    <v-data-table
      :items="items"
      :headers="headers"
      :server-items-length="total"
      :options.sync="optionsInternal"
      :loading="localLoading"
      :tab="tab"
      @update:options="emitOptions"
    >
      <template v-slot:item.payoutKey="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <router-link :to="{ name: 'payout', params: { id: item?.id } }">
                {{ item.payoutKey.slice(0, 8) }}
              </router-link>
            </span>
          </template>
          <span>{{ item.payoutKey }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.payoutAmount="{ item }">
        <span v-if="item.payoutAmount">
          {{ item.payoutAmount | toNumber(2) }}
          {{ item.currency ? item.currency?.name : "" }}
        </span>
      </template>

      <template v-slot:item.promotion.name="{ item }">
        <router-link
          v-if="$privilege.hasPrivilege('PROMOTION_READ')"
          :to="{ name: 'promotion', params: { id: item?.promotion?.id } }"
          >{{ item?.promotion?.promotionKey }} </router-link
        ><span v-else>{{ item?.promotion?.promotionKey }}</span
        ><br />
        {{ item?.promotion?.name }}
      </template>

      <template v-slot:item.participant="{ item }">
        <span v-if="item.participant && item.participant.id">
          <router-link
            v-if="$privilege.hasPrivilege('PARTICIPANT_READ')"
            :to="{ name: 'participant', params: { id: item.participant.id } }"
            >{{ item.participant.participantKey }} </router-link
          ><span v-else>{{ item.participant.participantKey }}</span
          ><br />
          {{ item.participant.fullName }}
        </span>
      </template>

      <template v-slot:item.organization="{ item }">
        <span v-if="item.organization && item.organization.id">
          <router-link
            v-if="$privilege.hasPrivilege('ORGANIZATION_READ')"
            :to="{ name: 'organization', params: { id: item.organization.id } }"
            >{{ item.organization.organizationKey }}</router-link
          ><span v-else>{{ item.organization.organizationKey }}</span
          ><br />
          {{ item?.organization?.name }}
        </span>
      </template>

      <template v-slot:item.approval="{ item }">
        <span v-if="item.approval">
          <router-link
            v-if="$privilege.hasPrivilege('APPROVAL_READ')"
            :to="{ name: 'approval', params: { id: item.approval.id } }"
            >{{ item.approval.approvalKey }}</router-link
          ><span v-else>{{ item.approval.approvalKey }}</span
          ><br />
          {{ item?.approval?.approvalStage?.name }}
        </span>
        <span v-else>
          No
        </span>
      </template>

      <template v-slot:item.relatesTo="{ item }">
        <span v-if="item.claimProduct">
          <router-link
            v-if="$privilege.hasPrivilege('PRODUCT_READ')"
            :to="{ name: 'product', params: { id: item.claimProduct.promotionProduct.product.id } }"
            >{{ item.claimProduct.promotionProduct.product.productKey }}</router-link
          ><span v-else>{{ item.claimProduct.promotionProduct.product.productKey }}</span
          ><br />

          {{ item?.claimProduct?.promotionProduct?.product?.name }}<br />
        </span>
        <span v-else-if="item.saleProduct">
          <span v-if="item.sale">
            Sale:<br />
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <router-link
                    v-if="$privilege.hasPrivilege('SALE_READ')"
                    :to="{ name: 'sale', params: { id: item.sale.id } }"
                  >
                    {{ item.sale.saleKey.slice(0, 10) + "..." }}
                  </router-link>
                  <span v-else>
                    {{ item.sale.saleKey.slice(0, 10) + "..." }}
                  </span>
                </span>
              </template>
              <span>{{ item.sale.saleKey }}</span>
            </v-tooltip>
          </span>
          Product:<br />
          <router-link
            v-if="$privilege.hasPrivilege('PRODUCT_READ')"
            :to="{ name: 'product', params: { id: item.saleProduct.product.id } }"
            >{{ item.saleProduct.product.productKey }}</router-link
          ><span v-else>{{ item.saleProduct.product.productKey }}</span
          ><br />
          {{ item?.saleProduct?.product?.name }}
        </span>
      </template>

      <template v-slot:item.bankTransaction="{ item }">
        <span v-if="item.bankTransaction">
          Bank Transaction:<br />
          <router-link
            v-if="$privilege.hasPrivilege('BANK_TRANSACTION_READ')"
            :to="{ name: 'bankTransaction', params: { id: item.bankTransaction.id } }"
            >{{ item.bankTransaction.bankTransactionKey }}</router-link
          ><span v-else>{{ item.bankTransaction.bankTransactionKey }}</span> <br /><br />
        </span>
        <span v-else>
          Not paid yet<br />
          <span v-if="item.pendingReason">{{ item.pendingReason }}<br /></span>

          <br />
        </span>
        <span v-if="item?.awardVehicle?.name">
          Award Vehicle:<br />
          {{ item?.awardVehicle?.name }}
        </span>
      </template>

      <template v-slot:item.updatedDate="{ item }">
        {{ item.updatedDate | formatDateFromNow }}
      </template>

      <template v-if="tab == 'expired'" v-slot:item.cancelledReason="{ item }">
        {{ item.cancelledReason }}
      </template>

      <template v-slot:item.actions="{ item }">
        <v-icon
          v-if="!disabled && !item.paid && $privilege.hasPrivilege('PAYOUT_DELETE')"
          @click="onDeleteConfirm(item)"
        >
          mdi-delete
        </v-icon>
        <v-tooltip bottom v-if="tab == 'expired' && $privilege.hasPrivilege('PAYOUT_UPDATE')">
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" @click="onExpiredConfirm(item)">mdi-arrow-right-circle-outline</v-icon>
          </template>
          <span>{{ $i18n.translate("Release & Pay") }}</span>
        </v-tooltip>
      </template>
    </v-data-table>
    <v-dialog v-if="deleteConfirmItem" v-model="deleteConfirm" max-width="650">
      <v-card>
        <v-card-title>
          Delete this payout?
        </v-card-title>
        <v-card-text>
          <p class="red--text" v-if="deleteConfirmItem.approval && !deleteConfirmItem.approval.closed">
            This payout is currently in an approval process. Close approval before deleting.
          </p>
          <b>Key</b>: {{ deleteConfirmItem.payoutKey }}<br />
          <b>Promotion</b>:
          {{
            deleteConfirmItem.promotion
              ? `${deleteConfirmItem.promotion.promotionKey} - ${deleteConfirmItem.promotion.name}`
              : ""
          }}<br />
          <b>Participant</b>:
          {{
            deleteConfirmItem.participant
              ? `${deleteConfirmItem.participant.participantKey} - ${deleteConfirmItem.participant.fullName}`
              : ""
          }}<br />
          <b>Product</b>:
          {{ deleteConfirmItem.claimProduct ? deleteConfirmItem.claimProduct.promotionProduct.product.productKey : "" }}
          {{ deleteConfirmItem.saleProduct ? deleteConfirmItem.saleProduct.product.productKey : "" }}
          <br />
          <b>Amount</b>: {{ deleteConfirmItem.payoutAmount | toNumber(2) }}
          {{ deleteConfirmItem.currency ? deleteConfirmItem.currency.name : "" }}<br />
          <b>Award Vehicle</b>: {{ deleteConfirmItem.awardVehicle.name ? deleteConfirmItem.awardVehicle.name : ""
          }}<br />
          <b>Under Approval</b>:
          {{ deleteConfirmItem.approval && !deleteConfirmItem.approval.closed ? "Yes" : "No" }}
          {{ deleteConfirmItem.approval ? " - " + deleteConfirmItem.approval.approvalKey : "" }}
          {{ deleteConfirmItem.approval ? " - " + deleteConfirmItem.approval.approvalStage.name : "" }}
        </v-card-text>

        <v-card-actions
          ><v-spacer></v-spacer
          ><v-btn
            class="primary"
            :disabled="deleteConfirmItem.approval && !deleteConfirmItem.approval.closed"
            @click="onDelete(deleteConfirmItem)"
            >Yes</v-btn
          ><v-btn @click="onDeleteCancel()">No</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="expireConfirm" v-model="expireConfirm" max-width="650">
      <v-card>
        <v-card-title>
          Release and pay this payout?
        </v-card-title>
        <v-card-text>
          <b>Key</b>: {{ expireConfirmItem.payoutKey }}<br />
          <b>Promotion</b>:
          {{
            expireConfirmItem.promotion
              ? `${expireConfirmItem.promotion.promotionKey} - ${expireConfirmItem.promotion.name}`
              : ""
          }}
          <br />
          <b>Participant</b>:
          {{
            expireConfirmItem.participant
              ? `${expireConfirmItem.participant.participantKey} - ${expireConfirmItem.participant.fullName}`
              : ""
          }}<br />
          <b>Product</b>:
          {{ expireConfirmItem.claimProduct ? expireConfirmItem.claimProduct.promotionProduct.product.productKey : "" }}
          {{ expireConfirmItem.saleProduct ? expireConfirmItem.saleProduct.product.productKey : "" }}
          <br />
          <b>Amount</b>: {{ expireConfirmItem.payoutAmount | toNumber(2) }}
          {{ expireConfirmItem.currency ? expireConfirmItem.currency.name : "" }}<br />
          <b>Award Vehicle</b>: {{ expireConfirmItem.awardVehicle.name ? expireConfirmItem.awardVehicle.name : ""
          }}<br />
          <b>Payout Cancelled Reason</b>: {{ expireConfirmItem.cancelledReason ?? "" }}<br />
          <v-select
            v-model="selectedSkipValidationType"
            :items="skipValidationOptions"
            item-text="text"
            item-value="value"
            label="Skip Validation (Optional)"
            clearable
            class="mt-4"
          ></v-select>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="primary" @click="onExpired(expireConfirmItem)" :loading="localLoading">Yes</v-btn>
          <v-btn @click="onExpiredCancel()">No</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ApiError from "@/gapp-components/components/display/ApiError.vue";
import { mapGetters } from "vuex";

export default {
  name: "PayoutTable",

  components: {
    ApiError
  },

  props: {
    participant: Object,
    organization: Object,
    nested: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    items: Array,
    loading: { type: Boolean, default: false },
    total: { type: Number, default: 0 },
    tab: { type: String, default: "all" },
    options: { type: Object, default: () => ({ itemsPerPage: 10, sortBy: ["updatedDate"], sortDesc: [true] }) }
  },
  data() {
    return {
      errors: {},
      expireConfirm: false,
      expireConfirmItem: null,
      deleteConfirm: false,
      deleteConfirmItem: null,
      localLoading: this.loading,
      optionsInternal: { ...this.options },
      selectedSkipValidationType: null,
      skipValidationOptions: [
        { text: "Course Requirements", value: "COURSE_REQUIREMENTS" },
        { text: "Approval", value: "APPROVAL" }
      ]
    };
  },
  methods: {
    onDeleteConfirm(item) {
      this.deleteConfirmItem = item;
      this.deleteConfirm = true;
    },
    onDeleteCancel() {
      this.deleteConfirmItem = null;
      this.deleteConfirm = false;
    },
    onDelete(item) {
      this.errors = {};
      this.localLoading = true;
      return this.$api
        .delete("/api/payouts/" + item.id + "/delete")
        .then(() => {
          this.$emit("refresh");
          this.$emit("delete-success");
        })
        .catch(error => {
          this.errors = this.$api.getErrorsFromResponse(error);
        })
        .finally(() => {
          this.onDeleteCancel();
          this.localLoading = false;
        });
    },
    onExpiredConfirm(item) {
      this.expireConfirmItem = item;
      this.expireConfirm = true;
    },

    onExpiredCancel() {
      this.expireConfirmItem = null;
      this.expireConfirm = false;
      this.selectedSkipValidationType = null;
    },

    onExpired(item) {
      this.errors = {};
      this.localLoading = true;
      return this.$api
        .post(`/api/payouts/${item.id}/addActivity`, {
          message: "Release & Pay initiated"
        })
        .then(() => {
          return this.$api.post(`/api/payouts/${item.id}/unexpire`);
        })
        .then(() => {
          const payload = {};
          if (this.selectedSkipValidationType) {
            payload.skipValidationTypes = [this.selectedSkipValidationType];
          }
          return this.$api
            .post(`/api/payouts/${item.id}/payPayouts`, payload)
            .then(() => {
              this.$emit("release-and-pay-success");
            })
            .catch(error => {
              this.errors = this.$api.getErrorsFromResponse(error);
            });
        })
        .catch(error => {
          this.errors = this.$api.getErrorsFromResponse(error);
        })
        .finally(() => {
          this.onExpiredCancel();
          this.localLoading = false;
        });
    },

    emitOptions(newOptions) {
      this.optionsInternal = newOptions;
      this.$emit("update:options", newOptions);
      this.$emit("refresh");
    }
  },
  watch: {
    loading(newVal) {
      this.localLoading = newVal;
    }
  },
  mounted() {
    if (!this.nested) {
      this.$store.dispatch("setBreadcrumb", [
        {
          text: this.$i18n.translate("Dashboard"),
          to: { name: "dashboard" },
          exact: true
        },
        {
          text: this.$i18n.translate("Clients"),
          to: { name: "clients" },
          exact: true
        },
        {
          text: this.selectedClient.name,
          to: {
            name: "client",
            params: { id: this.selectedClient.id },
            exact: true
          }
        },
        {
          text: this.$i18n.translate("Programs"),
          to: { name: "programs" },
          exact: true
        },
        {
          text: this.selectedProgram.name,
          to: {
            name: "program",
            params: { id: this.selectedProgram.id },
            exact: true
          }
        },
        { text: this.$i18n.translate("Payouts") }
      ]);
    }
  },
  computed: {
    ...mapGetters(["selectedClient", "selectedProgram"]),

    headers() {
      return [
        {
          value: "payoutKey",
          text: "Key",
          sortable: true
        },
        {
          value: "promotion.name",
          text: this.$i18n.translate("Promotion"),
          sortable: true
        },
        {
          value: "participant",
          text: this.$i18n.translate("Participant"),
          sortable: true
        },
        {
          value: "organization",
          text: this.$i18n.translate("Organization"),
          sortable: true
        },
        {
          value: "relatesTo",
          text: this.$i18n.translate("Relates To"),
          sortable: false
        },
        {
          value: "payoutAmount",
          text: this.$i18n.translate("Payout"),
          align: "right",
          sortable: true
        },
        {
          value: "bankTransaction",
          text: this.$i18n.translate("Payment"),
          sortable: false
        },
        {
          value: "approval",
          text: "Approval Needed?",
          sortable: true
        },
        {
          value: "updatedDate",
          text: this.$i18n.translate("Last Updated"),
          sortable: true
        },
        ...(this.tab === "expired"
          ? [
              {
                value: "cancelledReason",
                text: this.$i18n.translate("Expired Reason"),
                sortable: false
              }
            ]
          : []),
        {
          value: "actions",
          text: "",
          align: "center",
          sortable: false
        }
      ];
    }
  }
};
</script>
